import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline, Icon } from "@/components/primitives";
import { VINTAGE_BRAND_GROUPS } from "@/lib/brandReference";

// Browseable brand directory. Lists every brand Fig recognises as vintage.
// Click or search a brand → /brand/:name shows every public scan tagged
// with that brand across the platform. The page does NOT show details
// about the brand itself; that's a separate experience.

// Reference labels often have parenthetical qualifiers like
// "Juicy Couture (2001–2008)" or "Levi's (always approve)". The brand
// hub page (/brand/:name) does an ilike match against scans.brand, so
// strip the qualifier before routing.
function cleanBrandName(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*/g, "").trim();
}

type SortMode = "group" | "alpha";

export default function Brands() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>("group");

  const q = query.trim().toLowerCase();

  // Group view: keeps the editorial categories from brandReference. Searching
  // narrows each category to matching brands; categories with zero matches
  // drop out entirely so the body doesn't render empty headers.
  const grouped = useMemo(() => {
    if (!q) return VINTAGE_BRAND_GROUPS;
    const out: Record<string, string[]> = {};
    for (const [group, brands] of Object.entries(VINTAGE_BRAND_GROUPS)) {
      const matched = brands.filter(
        (b) => b.toLowerCase().includes(q) || group.toLowerCase().includes(q),
      );
      if (matched.length) out[group] = matched;
    }
    return out;
  }, [q]);

  // Alpha view: flatten every brand across every group, dedupe (some brands
  // appear in multiple groups), case-insensitive sort, then re-bucket by
  // first letter for findability.
  const alpha = useMemo(() => {
    const seen = new Set<string>();
    const all: string[] = [];
    for (const brands of Object.values(VINTAGE_BRAND_GROUPS)) {
      for (const b of brands) {
        const key = b.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        all.push(b);
      }
    }
    all.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    const filtered = q ? all.filter((b) => b.toLowerCase().includes(q)) : all;
    const buckets: Record<string, string[]> = {};
    for (const b of filtered) {
      const first = b.replace(/^["'`]/, "").charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : "#";
      (buckets[letter] ??= []).push(b);
    }
    return buckets;
  }, [q]);

  const openBrand = (brand: string) => {
    if (!brand) return;
    nav(`/brand/${encodeURIComponent(brand)}`);
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Header */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Brands</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Title */}
        <div style={{ padding: "14px 20px 0" }}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
            fontSize: 30, lineHeight: 1.05, color: "hsl(var(--ink))",
            letterSpacing: "-0.02em",
          }}>
            What Fig recognises
          </div>
          <div style={{
            marginTop: 6,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, color: "hsl(var(--ink-2))",
          }}>
            Tap any brand to see every public scan filed under it.
          </div>
        </div>

        {/* Search + sort toggle */}
        <div style={{ padding: "14px 20px 0" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a brand…"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, letterSpacing: "0.05em",
              color: "hsl(var(--ink))",
              outline: "none",
            }}
          />
          <div role="tablist" aria-label="Sort brands" style={{
            display: "flex", gap: 0, marginTop: 10,
          }}>
            {([
              { id: "group", label: "By group" },
              { id: "alpha", label: "A → Z" },
            ] as const).map((opt, i) => {
              const active = sort === opt.id;
              return (
                <button
                  key={opt.id}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => setSort(opt.id)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    background: active ? "hsl(var(--ink))" : "transparent",
                    color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    borderLeft: i === 0 ? "1px solid hsl(var(--ink))" : "none",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <Hairline dashed style={{ margin: "16px 20px 0" }} />

        {/* Body */}
        <div style={{ padding: "16px 20px 0", display: "flex", flexDirection: "column", gap: 16 }}>
          {sort === "group"
            ? Object.entries(grouped).map(([group, brands]) => (
                <BrandGroup key={group} group={group} brands={brands} onOpen={openBrand} />
              ))
            : Object.keys(alpha)
                .sort((a, b) => (a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b)))
                .map((letter) => (
                  <BrandGroup
                    key={letter}
                    group={letter}
                    brands={alpha[letter]}
                    onOpen={openBrand}
                  />
                ))}
          {((sort === "group" && Object.keys(grouped).length === 0) ||
            (sort === "alpha" && Object.keys(alpha).length === 0)) && <EmptyHit />}
        </div>
      </div>
    </MobileFrame>
  );
}

function BrandGroup({
  group, brands, onOpen,
}: {
  group: string; brands: string[]; onOpen: (brand: string) => void;
}) {
  return (
    <div>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 17,
        color: "hsl(var(--ink))", lineHeight: 1, marginBottom: 8,
      }}>
        {group}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {brands.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => onOpen(cleanBrandName(b))}
            title={`Open ${cleanBrandName(b)} hub`}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              padding: "3px 7px",
              border: "1px solid hsl(var(--ink))",
              background: "transparent",
              color: "hsl(var(--ink))",
              cursor: "pointer",
              display: "inline-block",
            }}
          >
            {b}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyHit() {
  return (
    <div style={{
      padding: "20px 16px", border: "1px dashed hsl(var(--ink-2))",
      textAlign: "center",
      fontFamily: "'Fraunces', 'Playfair Display', serif",
      color: "hsl(var(--ink-2))",
    }}>
      No brands match that search.
    </div>
  );
}
